import { useState, useRef } from 'react'
import { useAuth } from '../../../hooks/useAuth'
import { supabase } from '../../../lib/supabase'
import { Spinner } from '../../../components/ui/Spinner'
import { Portal } from '../../../components/Portal'
import { format } from 'date-fns'

const SERVICE_TYPES = [
  'oil_change','tire_rotation','brake_service','battery',
  'inspection','registration','insurance','repair','other',
]

const uploadReceipt = async (file, householdId, vehicleId) => {
  const path = `${householdId}/vehicles/receipts/${vehicleId}/${crypto.randomUUID()}_${file.name}`
  const { error } = await supabase.storage
    .from('household-documents')
    .upload(path, file, { upsert: false })
  if (error) throw error
  return path
}

export const ServiceRecordForm = ({ vehicleId, record, onSave, onClose, onCreate: _onCreate }) => {
  const { household } = useAuth()
  const fileRef = useRef(null)
  const today = format(new Date(), 'yyyy-MM-dd')

  const [serviceType,   setServiceType]   = useState(record?.service_type         ?? 'oil_change')
  const [description,   setDescription]   = useState(record?.description          ?? '')
  const [serviceDate,   setServiceDate]   = useState(record?.service_date         ?? today)
  const [mileage,       setMileage]       = useState(record?.mileage_at_service   ?? '')
  const [cost,          setCost]          = useState(record?.cost                 ?? '')
  const [provider,      setProvider]      = useState(record?.provider             ?? '')
  const [nextDate,      setNextDate]      = useState(record?.next_service_date    ?? '')
  const [nextMileage,   setNextMileage]   = useState(record?.next_service_mileage ?? '')
  const [notes,         setNotes]         = useState(record?.notes                ?? '')
  const [receiptFile,   setReceiptFile]   = useState(null)
  const [saving,        setSaving]        = useState(false)
  const [error,         setError]         = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!serviceDate) { setError('Service date is required.'); return }
    setSaving(true)
    setError('')
    try {
      let receipt_path = record?.receipt_path ?? null
      if (receiptFile) {
        receipt_path = await uploadReceipt(receiptFile, household.id, vehicleId)
      }
      const payload = {
        service_type:         serviceType,
        description:          description.trim()   || null,
        service_date:         serviceDate,
        mileage_at_service:   mileage  ? parseInt(mileage)         : null,
        cost:                 cost     ? parseFloat(cost)          : null,
        provider:             provider.trim()       || null,
        next_service_date:    nextDate              || null,
        next_service_mileage: nextMileage ? parseInt(nextMileage) : null,
        notes:                notes.trim()          || null,
        receipt_path,
      }
      await onSave(payload)
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
    }
  }

  const inputCls = 'w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
  const lbl = (t) => <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">{t}</label>

  return (
    <Portal>
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-text-primary">{record ? 'Edit Service Record' : 'Add Service Record'}</h2>
          <button type="button" onClick={onClose} className="text-text-muted p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          <div>
            {lbl('Service Type')}
            <select value={serviceType} onChange={(e) => setServiceType(e.target.value)}
              className={inputCls + ' bg-surface'}>
              {SERVICE_TYPES.map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>

          <div>{lbl('Description')}
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What was done…" className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>{lbl('Date *')}
              <input type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} className={inputCls} />
            </div>
            <div>{lbl('Mileage')}
              <input type="number" value={mileage} onChange={(e) => setMileage(e.target.value)}
                placeholder="45000" className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>{lbl('Cost ($)')}
              <input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)}
                placeholder="0.00" className={inputCls} />
            </div>
            <div>{lbl('Provider / Shop')}
              <input type="text" value={provider} onChange={(e) => setProvider(e.target.value)}
                placeholder="Jiffy Lube" className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>{lbl('Next Service Date')}
              <input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className={inputCls} />
            </div>
            <div>{lbl('Next Service Miles')}
              <input type="number" value={nextMileage} onChange={(e) => setNextMileage(e.target.value)}
                placeholder="48000" className={inputCls} />
            </div>
          </div>

          <div>{lbl('Notes')}
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={2} placeholder="Any additional notes…"
              className={inputCls + ' resize-none'} />
          </div>

          {/* Receipt */}
          <div>
            {lbl('Receipt')}
            <input ref={fileRef} type="file" className="hidden"
              onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)} />
            <button type="button" onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 text-sm text-primary font-medium">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
              </svg>
              {receiptFile ? receiptFile.name : 'Attach receipt'}
            </button>
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary">Cancel</button>
          <button type="submit" disabled={saving}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <><Spinner size="sm" /> Saving…</> : 'Save Record'}
          </button>
        </div>
      </form>
    </div>
    </Portal>
  )
}
